import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  getPlugin,
  removeNodes,
  insertText,
  focusEditor,
  findNodePath,
} from '@udecode/plate-common';

import APDropdown from 'components/Dropdown/APDropdown';
import { ELEMENT_MERGE_FIELD } from './createMergeFieldsPlugin';
import { getMergeFieldOnSelectItem } from './getMergeFieldOnSelectItem';

const onSelectItem = getMergeFieldOnSelectItem();

export const SmartInputElement = ({ children, editor, attributes, className, element }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef();
  const {
    options: { mergeFields },
  } = getPlugin(editor, ELEMENT_MERGE_FIELD);

  const options = useMemo(() => {
    return Object.keys(mergeFields).filter((label) => label.includes(search));
  }, [search]);

  const show = () => {
    setOpen(true);
  };

  const dismiss = () => {
    setOpen(false);
  };

  const searchIsValid = () => {
    const found = Object.keys(mergeFields).find(label => label === search);
    return !!found;
  };

  const handleFocus = () => {
    show();
  };

  const handleBlur = () => {
    dismiss();
    removeInput();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (searchIsValid()) {
      onSelectItem(editor, search, search);
      dismiss();
      return;
    }
    // Assume intention was to add regular text instead of a merge field
    // and just add the text already entered.
    setTimeout(() => {
      focusEditor(editor);
      insertText(editor, `{{${search}`);
    }, 0);
  };

  const handleItemSelect = (e, value) => {
    e.preventDefault();
    onSelectItem(editor, value, search);
    dismiss();
  };

  const handleChange = (e) => {
    setSearch(e.target.value);
  };

  const removeInput = () => {
    const path = findNodePath(editor, element);

    if (!path) return;

    removeNodes(editor, { at: path });
    focusEditor(editor);
  };

  useEffect(() => {
    if (inputRef?.current) {
      // Some kind of race condition is preventing the input
      // from being focused.
      setTimeout(() => {
        inputRef.current.focus();
      }, 50);
    }
  }, [inputRef]);


  return (
    <span
      contentEditable={false}
      className={className}
      {...attributes}
    >
      <span
        className="slate-smart-input__psudo-label"
      >
        {search}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={handleChange}
        className="slate-smart-input__input"
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {inputRef.current && (
        <APDropdown
          isOpen={open}
          show={show}
          dismiss={() => {}}
          root={null}
          anchorElement={inputRef.current}
          placement="top-start"
          dropDownClass='slate-smart-input__dropdown'
          constrainHeight={false}
        >
          {options.map((label, i) => {
            return (
              <button
                key={`merge-field-${i}`}
                onClick={(e) => handleItemSelect(e, label)}
                className="button--unstyled slate-smart-input__dropdown-item"
                onMouseDown={(e) => {
                  // To prevent the element dismissing before item is selected.
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {label}
              </button>
            );
          })}
        </APDropdown>
      )}
      {children}
    </span>
  );
};
